import { useSuspenseQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { queueQuery, taxEntryQuery } from '../../api/ops';
import { Hero, PageBody } from '../../shell';
import { Card, EmptyState, formatCount, formatWeekdayDate } from '../../ui';
import { QUEUE_VIEW } from '../labels';
import { QueueViews } from '../queue/QueueViews';
import { BackLink } from '../shared';
import { heroWords } from './presenter';
import s from './Tax.module.css';
import { PagedTaxEntryTable, useTaxSaver } from './TaxEntryRow';
import { ManageTabs } from '../../manage/ManageTabs';

const HELP = {
  shows:
    'Every PAA with cash between approval and its letters, one row per person: the gross, the taxes and net pay from ADP Quick Calc, the screenshot and who entered it. Always estimates.',
  canDo:
    'Type the taxes ADP shows (the net pay follows), or type over the net pay, then save the row. Attach the ADP screenshot; it stays private to the team. A figure can be changed until the letters go out.',
};

/**
 * Tax to enter (APP-101): any Stock Admin types each PAA person's taxes from ADP Quick Calc,
 * without claiming the award, a batch at a time. The letters wait until everyone has a figure.
 */
export function TaxEntryPage(): ReactElement {
  const { data: list } = useSuspenseQuery(taxEntryQuery);
  const { data: queue } = useSuspenseQuery(queueQuery('tax'));
  const saver = useTaxSaver();
  const waiting = list.awards.filter((a) => a.toEnter > 0);
  const entered = list.awards.filter((a) => a.toEnter === 0);

  return (
    <>
      <Hero
        eyebrow={`Stock administration · ${formatWeekdayDate(list.asOf)}`}
        title={QUEUE_VIEW.tax.label}
        sub={heroWords(list)}
        stats={[
          {
            label: 'All sectors · people to enter',
            value: formatCount(list.toEnter),
            meta: list.toEnter === 0 ? 'Nothing waiting on you' : 'Taxes from ADP Quick Calc',
            ...(list.toEnter > 0 && { tone: 'gold' as const }),
          },
          {
            label: 'All sectors · PAAs waiting on tax',
            value: formatCount(waiting.length),
            meta:
              waiting.length > 0
                ? 'Their letters wait on it'
                : list.reachedTeam
                  ? 'Every letter can be made'
                  : 'No PAA waiting yet',
          },
        ]}
        titleTestId="tax-title"
      />
      <ManageTabs current="queue" />
      <PageBody nav={<QueueViews queue={queue} queueHref="/manage/queue" />}>
        <div className={s.stack}>
          <BackLink href={QUEUE_VIEW.queue.href}>Queue</BackLink>
          <Card title="Still to enter" help={HELP}>
            {/* Empty, the header row stays and the day-one or caught-up message is its one row. */}
            <PagedTaxEntryTable
              table="queue"
              awards={waiting}
              caption="Still to enter"
              saver={saver}
              emptyState={
                list.reachedTeam ? (
                  <EmptyState
                    variant="caught-up"
                    size="sm"
                    headline="No PAA tax to enter"
                    body="Everyone on an approved PAA has their taxes entered. A new PAA lands here the moment it’s approved, and we email the team."
                  />
                ) : (
                  <EmptyState
                    variant="first-day"
                    art="calendar"
                    size="sm"
                    headline="No PAA tax to enter yet"
                    body="When a PAA with cash is approved, each person on it lands here for their taxes from ADP Quick Calc. The letters wait until everyone has one."
                  />
                )
              }
            />
          </Card>
          <Card
            title="Entered, letters not out yet"
            help={{
              shows:
                'PAAs whose people all have their taxes entered and whose letters haven’t gone out, so a figure can still be changed.',
              canDo:
                'Change a figure if ADP gives a different one. Once the letters go out, the figure on the letter stands and Payroll sets the final tax.',
            }}
          >
            <PagedTaxEntryTable
              prefix="entered"
              table={null}
              awards={entered}
              caption="Entered, letters not out yet"
              saver={saver}
              note={entered.length > 0}
              emptyState={
                <EmptyState
                  variant="first-day"
                  art="letter"
                  size="sm"
                  headline={
                    list.reachedTeam ? 'No PAA waiting on its letters' : 'Nothing entered yet'
                  }
                  body="A PAA moves here once everyone on it has their taxes entered, and stays until its letters go out, so a figure can still be changed."
                />
              }
            />
          </Card>
        </div>
      </PageBody>
    </>
  );
}
